import { alertBannerOptions, Job } from "./interfaces";
import { bindToggles } from "./rootcomponents/toggle";

export function getBoolean(value: any) {
    switch (value) {
        case true:
        case "true":
        case 1:
        case "1":
        case "on":
        case "yes":
            return true;
        default:
            return false;
    }
}

export function getRandomId(max: number = 9999) {
    return Math.floor(Math.random() * max).toString();
}

export function getCurrentTime() {
    return new Date().getTime();
}

export function isSameCalendarWeek(date1: Date, date2: Date): boolean {
    const getStartOfWeek = (date: Date) => {
        const startOfWeek = new Date(date);
        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        return startOfWeek;
    };

    return getStartOfWeek(date1).getTime() === getStartOfWeek(date2).getTime();
}

export function getStandardDeviation(values: number[]) {
    const n = values.length;
    if (n < 1) {
        return 0;
    }
    const mean = values.reduce((total, value) => { return total + value; }, 0) / n;

    return Math.sqrt(values.map(x => Math.pow(x - mean, 2)).reduce((total, value) => { return total + value; }, 0) / n);
}

export function padLeft(value: string | number, padCharacter: string, length: number) {
    let val = value.toString();
    while (val.length < length) {
        val = padCharacter + val;
    }
    return val;
}

export function scrollElementIntoView(elem: HTMLElement | string) {
    let theElem = typeof elem === 'string' ? document.querySelector<HTMLElement>(elem) : elem;
    if (theElem === null) {
        return;
    }
    window.scrollTo({ top: theElem?.offsetTop - 10, behavior: "smooth" });
}

export function getPrintableCharacters(str: string) {
    return str.replace(/[^\x20-\x7E]/g, "");
}

export function debounce<T extends Function>(callback: T, wait = 300) {
    let h: ReturnType<typeof setTimeout>;
    let callable = (...args: any) => {
        clearTimeout(h);
        h = setTimeout(() => callback(...args), wait);
    };
    return <T>(<any>callable);
}

export function isEmpty(value: string | any | null | undefined) {
    if (Array.isArray(value)) {
        return value === undefined || value === null || value.length < 1;
    }
    return value === undefined || value === null || value === "";
}

export const uiBreakpoints = {
    extraSmall: 480,
    small: 640,
    medium: 768,
    large: 1024,
    extraLarge: 1280,
    twoExtraLarge: 1536
}

export const defaults = {
    resultsPerPage: 25
}

export type HiringPathColors = {
    backgroundColorClass: string;
    textColorClass: string;
};

const defaultHiringPathColors: HiringPathColors = {
    backgroundColorClass: "bg-primary",
    textColorClass: "text-white"
};

const hiringPathColors: Record<string, HiringPathColors> = {
    disability: { backgroundColorClass: "bg-(--uswds-magenta-60)", textColorClass: "text-white" },
    fed: { backgroundColorClass: "bg-primary-darker", textColorClass: "text-white" },
    "fed-competitive": { backgroundColorClass: "bg-(--uswds-indigo-cool-50)", textColorClass: "text-white" },
    "fed-excepted": { backgroundColorClass: "bg-(--uswds-indigo-warm-50)", textColorClass: "text-white" },
    "fed-internal-nosearch": { backgroundColorClass: "bg-(--uswds-cyan-60v)", textColorClass: "text-white" },
    "fed-internal-search": { backgroundColorClass: "bg-(--uswds-cyan-60v)", textColorClass: "text-white" },
    "fed-transition": { backgroundColorClass: "bg-(--uswds-mint-cool-40v)", textColorClass: "text-black" },
    graduates: { backgroundColorClass: "bg-warning", textColorClass: "text-black" },
    land: { backgroundColorClass: "bg-(--uswds-gray-warm-70)", textColorClass: "text-white" },
    mspouse: { backgroundColorClass: "bg-success-dark", textColorClass: "text-white" },
    native: { backgroundColorClass: "bg-gray-dark", textColorClass: "text-white" },
    nguard: { backgroundColorClass: "bg-secondary-vivid", textColorClass: "text-white" },
    overseas: { backgroundColorClass: "bg-(--uswds-orange-warm-40)", textColorClass: "text-black" },
    peace: { backgroundColorClass: "bg-(--uswds-violet-70v)", textColorClass: "text-white" },
    public: { backgroundColorClass: "bg-accent-cool", textColorClass: "text-black" },
    "se-other": { backgroundColorClass: "bg-[#00BEE6]", textColorClass: "text-black" },
    ses: { backgroundColorClass: "bg-primary", textColorClass: "text-white" },
    "special-authorities": { backgroundColorClass: "bg-gray-light", textColorClass: "text-black" },
    student: { backgroundColorClass: "bg-warning", textColorClass: "text-black" },
    vet: { backgroundColorClass: "bg-success-dark", textColorClass: "text-white" }
};

export function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

export function htmlEncode(text: string) {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

export function isElementOnScreen(elem: HTMLElement): boolean {
    const rect = elem.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;

    const horizontallyVisible = rect.left < vw && rect.right > 0;
    const verticallyVisible = rect.top < vh && rect.bottom > 0;

    return horizontallyVisible && verticallyVisible;
}

export async function loadAlertBanner(alertElement: HTMLElement, options: alertBannerOptions) {
    const requestVerificationToken = getRequestVerificationToken();
    const response = await fetch('/notification/getalertbanner', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
        },
        body: JSON.stringify(options)
    });

    if (response.ok) {
        alertElement.innerHTML = await response.text();
        bindToggles(alertElement);
    }
}

/**
 * Gets the antiforgery token from the request-verification-token meta tag.
 * @returns antiforgery token
 */
export function getRequestVerificationToken(): string | undefined {
    return document.querySelector<HTMLMetaElement>('meta[name="request-verification-token"]')?.content;
}

/**
 * Gets an HTML string to render an svg icon from the spritesheet.
 * @param iconName The name of the icon
 * @param css Any CSS classes to be applied to the svg tag
 * @param role The aria role for the svg tag (defaults to presentation)
 * @returns HTML string for an svg tag
 */
export function getIconSvgTagFromSprite(iconName: string, css: string, role: string = "presentation"): string {
    return `<svg class="${css}" role="${role}"><use xlink:href="/img/icons/sprite.svg#${iconName}" /></svg>`;
}

let hiringPathsSpriteUrl: string | undefined;

export function getHiringPathsSpriteUrl(): string {
    if (hiringPathsSpriteUrl) {
        return hiringPathsSpriteUrl;
    }

    const element = document.querySelector<HTMLMetaElement>('meta[name="hiring-path-sprite-url"]');
    if (!element?.content) {
        throw new Error("Hiring-path sprite URL is unavailable.");
    }

    hiringPathsSpriteUrl = element.content;
    return hiringPathsSpriteUrl;
}

/**
 * Gets an HTML string to render an svg icon that is not in the spritesheet.
 * @param iconName The name of the icon
 * @param css Any CSS classes to be applied to the svg tag
 * @param role The aria role for the svg tag (defaults to presentation)
 * @returns HTML string for an svg tag
 */
export function getIconSvgTagFromNonSprite(iconName: string, css: string, role: string = "presentation"): string {
    return `<svg class="${css}" role="${role}"><use xlink:href="/img/icons/notsprite/${iconName}.svg" /></svg>`;
}

/**
 * Gets the background and text color classes associated with hiring path icons.
 * @param code The hiring path code to retrieve
 * @returns The HiringPathColors object corresponding to the specified hiring path code
 */
export function getHiringPathColors(code: string): HiringPathColors {
    return hiringPathColors[code] ?? defaultHiringPathColors;
}

/**
 * Gets the HTML for the start / end date clock
 * @param job The Job object to get the clock display for
 * @param [containerCss=''] Sets any non-layout CSS for the outside container of the display
 * @param [clockCss=''] Sets any non-fill CSS for the clock icon
 * @returns HTML for the clock display
 */
export function getJobClockDisplay(job: Job, containerCss: string = '', clockCss: string = ''): string {
    clockCss = clockCss.trim() || 'w-4 h-4';
    containerCss = `${containerCss.trim()} flex flex-nowrap items-center gap-x-1`;

    switch (job.ClockDisplay) {
        case "JobClosingIn7Days":
            clockCss += ' fill-warning';
            break;
        case "JobClosed":
        case "JobClosingIn3Days":
            clockCss += ' fill-emergency';
            break;
        default:
            clockCss += ' fill-gray-dark';
    }

    return `<div class="${containerCss}">${getIconSvgTagFromSprite('schedule', clockCss)} <span>${job.DateDisplay}</span></div>`;
}
